import { Color3, Curve3, GizmoManager, Mesh, MeshBuilder, PointerEventTypes, StandardMaterial, Vector3 } from '@babylonjs/core';
import { AdvancedDynamicTexture, Button, Control, Rectangle, Slider, StackPanel, TextBlock } from '@babylonjs/gui';
import { Game } from '../Game';
import { Level } from '../Level';
import { Gui } from '../gui/Gui';

/**
 * Interactive path editor tool for SceneViewer mode.
 * Allows creating and editing control points that are smoothed into a Catmull-Rom spline.
 * The resulting path can be exported as TypeScript or JSON for use in PathConfig.predefined.
 */
export class PathEditorTool {
  private controlPoints: Mesh[] = [];
  private previewMesh: Mesh | null = null;
  private pickingPlane: Mesh | null = null;
  private segmentsPerCurve: number = 10;
  private gizmoManager: GizmoManager;
  private gui: AdvancedDynamicTexture;
  private countText: TextBlock;

  // Visual settings
  private readonly CONTROL_POINT_SIZE = 1;
  private readonly CONTROL_POINT_COLOR = new Color3(1, 0.5, 0);
  private readonly SELECTED_COLOR = new Color3(0, 1, 0);
  private readonly PREVIEW_COLOR = new Color3(0.3, 0.7, 1);

  constructor(private level: Level) {}

  /**
   * Initialize the path editor tool
   */
  initialize(): void {
    this.createGizmoManager();
    this.createGUI();
    this.setupPointerEvents();
    this.setupKeyboardEvents();
  }

  /**
   * Create the gizmo manager for moving control points
   */
  private createGizmoManager(): void {
    this.gizmoManager = new GizmoManager(this.level.scene);
    this.gizmoManager.positionGizmoEnabled = true;
    this.gizmoManager.rotationGizmoEnabled = false;
    this.gizmoManager.scaleGizmoEnabled = false;
    this.gizmoManager.usePointerToAttachGizmos = false; // We'll manage attachment ourselves

    // Update preview when gizmo moves a control point
    if (this.gizmoManager.gizmos.positionGizmo) {
      this.gizmoManager.gizmos.positionGizmo.onDragEndObservable.add(() => {
        this.updatePreview();
      });
    }
  }

  /**
   * Create the GUI panel
   */
  private createGUI(): void {
    this.gui = AdvancedDynamicTexture.CreateFullscreenUI('PathEditorUI', true, this.level.scene);

    // Main panel
    const panel = new Rectangle('pathEditorPanel');
    panel.width = '250px';
    panel.height = '230px';
    panel.cornerRadius = 10;
    panel.color = 'white';
    panel.thickness = 2;
    panel.background = Gui.controlBackground;
    panel.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
    panel.verticalAlignment = Control.VERTICAL_ALIGNMENT_TOP;
    panel.left = '10px';
    panel.top = '50px';
    this.gui.addControl(panel);

    // Stack panel for content
    const stack = new StackPanel('contentStack');
    stack.width = '230px';
    panel.addControl(stack);

    // Title
    const title = new TextBlock('title', '🛤️ Path Editor');
    title.height = '30px';
    title.fontSize = 18;
    title.color = 'white';
    title.fontWeight = 'bold';
    stack.addControl(title);

    // Control point count
    this.countText = new TextBlock('count', 'Control Points: 0');
    this.countText.height = '25px';
    this.countText.fontSize = 14;
    this.countText.color = 'lightgray';
    stack.addControl(this.countText);

    // Segments slider label
    const sliderLabel = new TextBlock('sliderLabel', 'Segments per Curve: 10');
    sliderLabel.height = '25px';
    sliderLabel.fontSize = 12;
    sliderLabel.color = 'lightgray';
    stack.addControl(sliderLabel);

    // Segments slider
    const slider = new Slider('segmentsSlider');
    slider.minimum = 2;
    slider.maximum = 50;
    slider.value = this.segmentsPerCurve;
    slider.height = '30px';
    slider.width = '200px';
    slider.color = 'orange';
    slider.background = 'gray';
    slider.onValueChangedObservable.add((value) => {
      this.segmentsPerCurve = Math.round(value);
      sliderLabel.text = `Segments per Curve: ${this.segmentsPerCurve}`;
      this.updatePreview();
    });
    stack.addControl(slider);

    // Spacer
    const spacer1 = new Rectangle('spacer1');
    spacer1.height = '15px';
    spacer1.thickness = 0;
    stack.addControl(spacer1);

    // Button row for Generate and Clear
    const actionRow = new StackPanel('actionRow');
    actionRow.isVertical = false;
    actionRow.height = '35px';
    actionRow.width = '200px';
    stack.addControl(actionRow);

    // Generate button
    const generateBtn = Button.CreateSimpleButton('generateBtn', 'Generate');
    generateBtn.width = '95px';
    generateBtn.height = '35px';
    generateBtn.color = 'white';
    generateBtn.background = 'green';
    generateBtn.cornerRadius = 5;
    generateBtn.onPointerUpObservable.add(() => this.createCode());
    actionRow.addControl(generateBtn);

    // Spacer
    const spacer2 = new Rectangle('spacer2');
    spacer2.width = '10px';
    spacer2.thickness = 0;
    actionRow.addControl(spacer2);

    // Clear button
    const clearBtn = Button.CreateSimpleButton('clearBtn', 'Clear');
    clearBtn.width = '95px';
    clearBtn.height = '35px';
    clearBtn.color = 'white';
    clearBtn.background = '#c0392b';
    clearBtn.cornerRadius = 5;
    clearBtn.onPointerUpObservable.add(() => this.clearAllPoints());
    actionRow.addControl(clearBtn);

    // Spacer
    const spacer3 = new Rectangle('spacer3');
    spacer3.height = '10px';
    spacer3.thickness = 0;
    stack.addControl(spacer3);

    // Help text
    const helpText = new TextBlock('help', 'Shift+Click: Add point\nRight-click: Delete point');
    helpText.height = '40px';
    helpText.fontSize = 11;
    helpText.color = 'gray';
    stack.addControl(helpText);
  }

  /**
   * Setup pointer events for adding/removing control points
   */
  private setupPointerEvents(): void {
    // Create an invisible large ground plane for picking when no other geometry exists
    this.createPickingPlane();

    this.level.scene.onPointerObservable.add((pointerInfo) => {
      if (pointerInfo.type === PointerEventTypes.POINTERDOWN) {
        // Temporarily enable picking on the level ground mesh (it's set to isPickable=false by MeshFactory)
        const ground = this.level.ground;
        const groundWasPickable = ground?.isPickable;
        if (ground) {
          ground.isPickable = true;
        }

        // Use multiPick to get all hits, so we can prefer real ground over flat picking plane
        const pickResults = this.level.scene.multiPick(
          this.level.scene.pointerX,
          this.level.scene.pointerY,
          (mesh) => mesh.isPickable && mesh.isEnabled()
        );

        // Restore original pickable state
        if (ground) {
          ground.isPickable = groundWasPickable;
        }

        if (!pickResults || pickResults.length === 0) {
          console.log('[PathEditorTool] No hit detected');
          return;
        }

        // Filter to valid hits
        const validHits = pickResults.filter((r) => r.hit && r.pickedPoint && r.pickedMesh);
        if (validHits.length === 0) {
          console.log('[PathEditorTool] No valid hit detected');
          return;
        }

        // Check if any hit is a control point (highest priority)
        const controlPointHit = validHits.find((r) => (r.pickedMesh as Mesh)?.metadata?.isControlPoint);
        if (controlPointHit) {
          const pickedMesh = controlPointHit.pickedMesh as Mesh;
          console.log(`[PathEditorTool] Picked control point: ${pickedMesh.name}`);
          if (pointerInfo.event.button === 2) {
            this.removeControlPoint(pickedMesh);
          } else if (pointerInfo.event.button === 0) {
            this.selectControlPoint(pickedMesh);
          }
          return;
        }

        // For ground picking: prefer real ground/scene meshes over the flat picking plane
        // Sort: real meshes first, then flat picking plane as fallback
        const sortedHits = validHits
          .filter((r) => (r.pickedMesh as Mesh)?.name !== 'pathPreview')
          .sort((a, b) => {
            const aIsPickingPlane = a.pickedMesh === this.pickingPlane;
            const bIsPickingPlane = b.pickedMesh === this.pickingPlane;
            if (aIsPickingPlane && !bIsPickingPlane) return 1; // b first
            if (!aIsPickingPlane && bIsPickingPlane) return -1; // a first
            return (a.distance || 0) - (b.distance || 0); // closer first
          });

        const bestHit = sortedHits[0];
        if (!bestHit) return;

        const pickedMesh = bestHit.pickedMesh as Mesh;
        console.log(`[PathEditorTool] Picked: ${pickedMesh?.name || 'unknown'} at ${bestHit.pickedPoint.toString()}`);

        // Shift + Left-click on ground/other mesh: add new control point
        if (pointerInfo.event.button === 0 && pointerInfo.event.shiftKey && bestHit.pickedPoint) {
          this.addControlPoint(bestHit.pickedPoint);
        }
      }
    });

    // Prevent context menu on right-click
    this.level.scene
      .getEngine()
      .getRenderingCanvas()
      ?.addEventListener('contextmenu', (e) => {
        e.preventDefault();
      });
  }

  /**
   * Create an invisible ground plane for picking
   */
  private createPickingPlane(): void {
    this.pickingPlane = MeshBuilder.CreateGround('pathEditorGround', { width: 2000, height: 2000 }, this.level.scene);
    this.pickingPlane.position.y = 0;
    this.pickingPlane.isPickable = true;
    this.pickingPlane.visibility = 0; // Invisible but still pickable

    // Make it receive picks but not render
    const material = new StandardMaterial('groundMat', this.level.scene);
    material.alpha = 0;
    this.pickingPlane.material = material;

    console.log('[PathEditorTool] Created invisible picking ground plane (fallback)');
  }

  /**
   * Setup keyboard events
   */
  private setupKeyboardEvents(): void {
    this.level.scene.onKeyboardObservable.add((kbInfo) => {
      if (kbInfo.type === 1) {
        // Key down
        switch (kbInfo.event.key.toLowerCase()) {
          case 'escape':
            this.gizmoManager.attachToMesh(null);
            break;
        }
      }
    });
  }

  /**
   * Add a new control point at the given position
   */
  addControlPoint(position: Vector3): void {
    const index = this.controlPoints.length;
    const sphere = MeshBuilder.CreateSphere(`controlPoint_${index}`, { diameter: this.CONTROL_POINT_SIZE }, this.level.scene);
    sphere.position = position.clone();

    // Material
    const material = new StandardMaterial(`controlPointMat_${index}`, this.level.scene);
    material.diffuseColor = this.CONTROL_POINT_COLOR;
    material.emissiveColor = this.CONTROL_POINT_COLOR.scale(0.5);
    sphere.material = material;

    // Metadata for identification
    sphere.metadata = { isControlPoint: true, index: index };

    // Make it pickable
    sphere.isPickable = true;

    this.controlPoints.push(sphere);
    this.updateCountText();
    this.updatePreview();

    console.log(`[PathEditorTool] Added control point ${index} at ${position.toString()}`);
  }

  /**
   * Select a control point for gizmo manipulation
   */
  private selectControlPoint(mesh: Mesh): void {
    // Reset all materials
    this.controlPoints.forEach((cp) => {
      const mat = cp.material as StandardMaterial;
      mat.diffuseColor = this.CONTROL_POINT_COLOR;
      mat.emissiveColor = this.CONTROL_POINT_COLOR.scale(0.5);
    });

    // Highlight selected
    const mat = mesh.material as StandardMaterial;
    mat.diffuseColor = this.SELECTED_COLOR;
    mat.emissiveColor = this.SELECTED_COLOR.scale(0.5);

    // Attach gizmo
    this.gizmoManager.attachToMesh(mesh);
  }

  /**
   * Remove a control point
   */
  removeControlPoint(mesh: Mesh): void {
    const index = this.controlPoints.indexOf(mesh);
    if (index === -1) return;

    // Detach gizmo if attached to this mesh
    if (this.gizmoManager.attachedMesh === mesh) {
      this.gizmoManager.attachToMesh(null);
    }

    this.controlPoints.splice(index, 1);
    mesh.dispose();

    // Update indices
    this.controlPoints.forEach((cp, i) => {
      cp.metadata.index = i;
    });

    this.updateCountText();
    this.updatePreview();

    console.log(`[PathEditorTool] Removed control point ${index}`);
  }

  /**
   * Clear all control points
   */
  clearAllPoints(): void {
    this.gizmoManager.attachToMesh(null);
    this.controlPoints.forEach((cp) => cp.dispose());
    this.controlPoints = [];
    this.updateCountText();
    this.updatePreview();
    console.log('[PathEditorTool] Cleared all control points');
  }

  /**
   * Update the control point count display
   */
  private updateCountText(): void {
    this.countText.text = `Control Points: ${this.controlPoints.length}`;
  }

  /**
   * Update the spline preview mesh
   */
  updatePreview(): void {
    // Dispose old preview
    if (this.previewMesh) {
      this.previewMesh.dispose();
      this.previewMesh = null;
    }

    // Need at least 2 points for a spline
    if (this.controlPoints.length < 2) return;

    const positions = this.controlPoints.map((cp) => cp.position.clone());
    const curve = Curve3.CreateCatmullRomSpline(positions, this.segmentsPerCurve, false);
    const pathPoints = curve.getPoints();

    // Create tube mesh for preview
    this.previewMesh = MeshBuilder.CreateTube(
      'pathPreview',
      {
        path: pathPoints,
        radius: 0.2,
        tessellation: 8,
        updatable: false,
      },
      this.level.scene
    );

    const material = new StandardMaterial('previewMat', this.level.scene);
    material.diffuseColor = this.PREVIEW_COLOR;
    material.emissiveColor = this.PREVIEW_COLOR.scale(0.3);
    material.alpha = 0.7;
    this.previewMesh.material = material;
    this.previewMesh.isPickable = false;
  }

  /**
   * Generate the final path from control points
   */
  generatePath(): Vector3[] {
    if (this.controlPoints.length < 2) {
      console.warn('[PathEditorTool] Need at least 2 control points to generate a path');
      return [];
    }

    const positions = this.controlPoints.map((cp) => cp.position.clone());
    const curve = Curve3.CreateCatmullRomSpline(positions, this.segmentsPerCurve, false);
    return curve.getPoints();
  }

  /**
   * Create code for the path
   */
  createCode(): void {
    const path = this.generatePath();
    if (path.length === 0) {
      console.warn('[PathEditorTool] No path to generate. Add at least 2 control points.');
      return;
    }

    // TypeScript format
    const tsFormat = path.map((p) => `new Vector3(${p.x.toFixed(3)}, ${p.y.toFixed(3)}, ${p.z.toFixed(3)})`).join(',\n  ');

    // Build message string
    let message = '========== Path Editor Output ==========\n\n';
    message += `📐 Generated ${path.length} points from ${this.controlPoints.length} control points\n\n`;

    message += '📝 TypeScript Format (for predefined path):\n';
    message += `[\n  ${tsFormat}\n]\n`;

    // JSON format
    const jsonFormat = path.map((p) => ({
      x: parseFloat(p.x.toFixed(3)),
      y: parseFloat(p.y.toFixed(3)),
      z: parseFloat(p.z.toFixed(3)),
    }));

    message += '\n📋 JSON Format:\n';
    message += JSON.stringify(jsonFormat, null, 2);

    // Control points only (for manual editing)
    const controlPointsTs = this.controlPoints
      .map((cp) => `new Vector3(${cp.position.x.toFixed(3)}, ${cp.position.y.toFixed(3)}, ${cp.position.z.toFixed(3)})`)
      .join(',\n  ');

    message += '\n\n🎯 Control Points Only (TypeScript):\n';
    message += `[\n  ${controlPointsTs}\n]`;

    message += '\n\n=========================================';

    Game.fireMessage(message);
  }

  /**
   * Dispose the tool and clean up resources
   */
  dispose(): void {
    this.clearAllPoints();
    if (this.previewMesh) {
      this.previewMesh.dispose();
    }
    if (this.pickingPlane) {
      this.pickingPlane.dispose();
    }
    this.gizmoManager.dispose();
    this.gui.dispose();
  }
}
